import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutDateTimeInput } from 'components/design-system/BreakoutDateTimeInput'
import { FullPageSpinner } from 'components/Spinner'
import { useInstructorSectionCubit } from 'hooks/cubits/instructorSection'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export const SectionActivationScreen = observer(
  function SectionActivationScreen() {
    const sectionCubit = useInstructorSectionCubit()
    const [hasFailed, setHasFailed] = useState(false)

    useEffect(() => {
      if (hasFailed) return

      try {
        sectionCubit.markAsNotStarted()
      } catch (error) {
        setHasFailed(true)
      }
    }, [hasFailed, sectionCubit])

    return <FullPageSpinner reason="lti_section_configuration_loading" />
  }
)

export const SectionDatesConfiguration = observer(
  function SectionDatesConfiguration() {
    const sectionCubit = useInstructorSectionCubit()
    const { t } = useTranslation()
    const [errors, setErrors] = useState<
      Record<string, FieldError> | undefined
    >(undefined)
    const formRef = useRef<HTMLFormElement>(null)

    const validate = useCallback(() => {
      setErrors(undefined)

      const form = formRef.current
      if (!form)
        return {
          startDateRaw: undefined,
          endDateRaw: undefined,
          errors: undefined,
          isValid: false,
        }

      const newErrors: Record<string, FieldError> = {}
      const formData = new FormData(form)

      const startDateRaw = formData.get('sectionStartDate')
      if (!startDateRaw) {
        newErrors.sectionStartDate = {
          type: 'required',
          message: t('lti.configuration.section_start_date_required'),
        }
      }

      const endDateRaw = formData.get('sectionEndDate')
      if (!endDateRaw) {
        newErrors.sectionEndDate = {
          type: 'required',
          message: t('lti.configuration.section_end_date_required'),
        }
      }

      if (startDateRaw && endDateRaw) {
        const startDate = DateTime.fromISO(startDateRaw as string)
        const endDate = DateTime.fromISO(endDateRaw as string)
        if (startDate > endDate) {
          newErrors.sectionEndDate = {
            type: 'invalid',
            message: t('lti.configuration.section_end_date_in_past'),
          }
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        return {
          startDateRaw,
          endDateRaw,
          errors: newErrors,
          isValid: false,
        }
      }

      return {
        startDateRaw,
        endDateRaw,
        errors: newErrors,
        isValid: Object.keys(newErrors).length === 0,
      }
    }, [t])

    const onSubmit = useCallback(
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const { isValid, startDateRaw, endDateRaw } = validate()

        if (!isValid) return

        const sectionStartDate = DateTime.fromISO(startDateRaw as string)
        const sectionEndDate = DateTime.fromISO(endDateRaw as string)

        await sectionCubit.updateSection({
          sectionStartDate,
          sectionEndDate,
        })
      },
      [sectionCubit, validate]
    )

    return (
      <div className="section flex min-h-full w-full flex-col items-start justify-center">
        <div className="ml-[5vw] max-w-[400px]">
          <div className="mb-5">
            <h1 className="text-headline-large">
              {t('lti.configuration.add_section_dates')}
            </h1>
            <div className="text-body-large">
              {t('lti.configuration.add_section_dates_description')}
            </div>
          </div>
          <form
            ref={formRef}
            onSubmit={onSubmit}
            className="flex flex-col gap-5"
          >
            <BreakoutDateTimeInput
              label={t('lti.configuration.section_start_date')}
              name="sectionStartDate"
              resolution="15min"
              error={errors?.sectionStartDate}
              initialView={DateTime.now().startOf('day')}
              onChange={() => setTimeout(validate, 0)}
            />
            <BreakoutDateTimeInput
              label={t('lti.configuration.section_end_date')}
              name="sectionEndDate"
              resolution="15min"
              error={errors?.sectionEndDate}
              initialView={DateTime.now().startOf('day').plus({ months: 6 })}
              onChange={() => setTimeout(validate, 0)}
            />

            <BreakoutButton
              disabled={errors !== undefined}
              kind="accent"
              type="submit"
              size="medium"
              className="bg-fixed-accent-color"
            >
              {t('lti.configuration.continue')}
            </BreakoutButton>
          </form>
        </div>
      </div>
    )
  }
)
